import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiX, FiDownload, FiCheck, FiFileText } = FiIcons;

const CSVUploader = ({ onClose, onSuccess }) => {
  const [uploadMethod, setUploadMethod] = useState('file'); // 'file' or 'text'
  const [csvText, setCsvText] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState([]);
  const [importSummary, setImportSummary] = useState(null);

  const resetState = () => {
    setFile(null);
    setCsvText('');
    setError('');
    setPreview([]);
    setImportSummary(null);
  };

  // A robust CSV line parser that handles quoted fields, commas, and escaped quotes ("")
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuote = false;

    // Use a simple state machine approach for parsing
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuote && line[i + 1] === '"') {
          // Handle escaped quote: "" -> "
          current += '"';
          i++; // Skip the next quote
        } else {
          // Toggle quote state
          inQuote = !inQuote;
        }
      } else if (char === ',' && !inQuote) {
        // Delimiter found outside quotes
        result.push(current.trim());
        current = '';
      } else {
        // Regular character or character inside quotes
        current += char;
      }
    }
    // Push the last field
    result.push(current.trim());
    return result;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      resetState();
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = (event) => parseCSVPreview(event.target.result);
      reader.readAsText(selectedFile);
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
      setPreview([]);
    }
  };

  const handleTextChange = (e) => {
    resetState();
    const text = e.target.value;
    setCsvText(text);
    if (text.trim()) {
      parseCSVPreview(text);
    } else {
      setPreview([]);
    }
  };

  const parseCSVPreview = (textContent) => {
    if (!textContent) {
      setPreview([]);
      return;
    }

    // Normalize line endings and split
    const lines = textContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(line => line.trim());
    if (lines.length < 1) {
      setPreview([]);
      return;
    }

    try {
      const headers = parseCSVLine(lines[0]);
      if (headers.length < 2) {
        throw new Error('CSV header must have at least Name and Handle columns.');
      }

      const rows = lines.slice(1, 6).map(line => {
        const values = parseCSVLine(line);
        // Ensure values array matches headers length, padding with empty strings if necessary
        while (values.length < headers.length) {
          values.push('');
        }
        return headers.reduce((obj, header, index) => {
          obj[header] = values[index] || '';
          return obj;
        }, {});
      });

      setPreview(rows);
      setError(''); // Clear any previous errors
    } catch (parseError) {
      setError(`Failed to parse CSV: ${parseError.message}`);
      setPreview([]);
    }
  };

  const handleUpload = async () => {
    const textContent = uploadMethod === 'file' && file ? await file.text() : csvText;
    if (!textContent || !textContent.trim()) {
      setError('No data to upload.');
      return;
    }

    setUploading(true);
    setError('');
    setImportSummary(null);

    try {
      // Normalize line endings and filter empty lines
      const lines = textContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        throw new Error('CSV must have a header and at least one data row.');
      }

      const headers = parseCSVLine(lines[0]);
      if (headers.length < 2) {
        throw new Error('CSV header must have at least Name and Handle columns.');
      }

      const profiles = [];
      let skipped = 0;
      let successful = 0;
      let malformedRows = 0;

      for (let i = 1; i < lines.length; i++) {
        try {
          const values = parseCSVLine(lines[i]);
          if (values.length !== headers.length) {
            console.warn(`Skipping malformed row ${i + 1}: Expected ${headers.length} columns, got ${values.length}`);
            malformedRows++;
            continue;
          }

          const profile = headers.reduce((obj, header, index) => {
            obj[header] = values[index] || '';
            return obj;
          }, {});

          const name = profile.Name || profile.name || '';
          const handle = (profile.Handle || profile.handle || '').replace('@', '');
          const profile_pic_url = profile['Profile Pic URL'] || profile.profile_pic_url || '';

          const tags = [];
          for (let j = 0; j < headers.length; j++) {
            const header = headers[j];
            if (header.toLowerCase().startsWith('tag') && profile[header]) {
              tags.push(profile[header].trim());
            }
          }

          if (name && handle) {
            const { data: existingProfile } = await supabase
              .from('profiles')
              .select('id')
              .eq('instagram_handle', handle)
              .single();

            if (existingProfile) {
              skipped++;
              continue;
            }

            profiles.push({
              name,
              instagram_handle: handle,
              bio: profile.Bio || profile.bio || '',
              profile_pic_url: profile_pic_url.trim() || null,
              tags: tags.filter(Boolean),
              status: 'pending',
            });
            successful++;
          } else {
            malformedRows++;
          }
        } catch (rowError) {
          console.warn(`Error processing row ${i + 1}: ${rowError.message}`);
          malformedRows++;
          continue;
        }
      }

      // Deduplicate profiles within the array to prevent unique constraint violations
      const uniqueProfiles = profiles.filter(
        (profile, index, self) => index === self.findIndex(p => p.instagram_handle === profile.instagram_handle)
      );

      // Collect all unique tags from the uploaded profiles
      const allUploadTags = uniqueProfiles.flatMap(p => p.tags || []);
      const uniqueUploadTags = [...new Set(allUploadTags)];

      if (uniqueUploadTags.length > 0) {
        // Fetch existing tags to avoid duplicates
        const { data: existingTagsData, error: fetchTagsError } = await supabase
          .from('tags')
          .select('name')
          .in('name', uniqueUploadTags);

        if (fetchTagsError) throw fetchTagsError;

        const existingTagNames = existingTagsData.map(t => t.name);

        // Determine which tags are new
        const newTagsToInsert = uniqueUploadTags
          .filter(tag => !existingTagNames.includes(tag))
          .map(name => ({ name }));

        // Insert new tags if any
        if (newTagsToInsert.length > 0) {
          const { error: insertTagsError } = await supabase
            .from('tags')
            .insert(newTagsToInsert);

          if (insertTagsError && insertTagsError.code !== '23505') { // Ignore unique constraint errors
            throw insertTagsError;
          }
        }
      }

      if (uniqueProfiles.length > 0) {
        const { error: insertError } = await supabase
          .from('profiles')
          .insert(uniqueProfiles);

        if (insertError) throw insertError;
      }

      setImportSummary({ successful: uniqueProfiles.length, skipped, malformedRows });
      onSuccess();
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setError(`Error processing CSV data: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'Name,Handle,Bio,Profile Pic URL,Tag1,Tag2,Tag3\n"John Doe","johndoe","Photographer, traveler, and blogger","https://example.com/pic.jpg","Photography","Travel","Lifestyle"';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'instagram-profiles-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Import Profiles</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-blue-900">Need a template?</h3>
                  <p className="text-sm text-blue-700">
                    Download our CSV template to get started
                  </p>
                </div>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <SafeIcon icon={FiDownload} />
                  <span>Template</span>
                </button>
              </div>
            </div>

            <div className="flex border border-gray-200 rounded-lg p-1 bg-gray-100">
              <button
                onClick={() => { setUploadMethod('file'); resetState(); }}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center space-x-2 ${uploadMethod === 'file' ? 'bg-white text-yellow-700 shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`}
              >
                <SafeIcon icon={FiUpload} />
                <span>Upload File</span>
              </button>
              <button
                onClick={() => { setUploadMethod('text'); resetState(); }}
                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors flex items-center justify-center space-x-2 ${uploadMethod === 'text' ? 'bg-white text-yellow-700 shadow-sm' : 'text-gray-600 hover:bg-gray-200'}`}
              >
                <SafeIcon icon={FiFileText} />
                <span>Paste Text</span>
              </button>
            </div>

            {uploadMethod === 'file' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-yellow-500 transition-colors">
                  <SafeIcon icon={FiUpload} className="mx-auto text-4xl text-gray-400 mb-4" />
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="cursor-pointer text-yellow-500 hover:text-yellow-600 font-medium"
                  >
                    Click to upload CSV file
                  </label>
                  <p className="text-sm text-gray-500 mt-2">
                    CSV format: Name,Handle,Bio,Profile Pic URL,Tag1,Tag2...
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste CSV Data
                </label>
                <textarea
                  value={csvText}
                  onChange={handleTextChange}
                  placeholder={'Name,Handle,Bio,Profile Pic URL,Tag1,Tag2...\n"John Doe","johndoe","Bio...","https://...",Photography,Travel'}
                  rows={8}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:border-yellow-500 resize-vertical font-mono text-sm"
                />
              </div>
            )}

            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

            {preview.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Preview</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          {Object.keys(preview[0] || {}).map(header => (
                            <th key={header} className="px-4 py-2 text-left font-medium text-gray-700">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {preview.map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value, i) => (
                              <td key={i} className="px-4 py-2 truncate max-w-xs">
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {importSummary && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <SafeIcon icon={FiCheck} className="text-green-600" />
                  <span className="font-medium text-green-900">Import Complete</span>
                </div>
                <p className="text-sm text-green-700">
                  Added {importSummary.successful} profiles, skipped {importSummary.skipped} duplicates
                  {importSummary.malformedRows > 0 ? `, skipped ${importSummary.malformedRows} malformed rows` : ''}.
                </p>
              </div>
            )}

            <div className="flex justify-end space-x-4 pt-4">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || (uploadMethod === 'file' && !file) || (uploadMethod === 'text' && !csvText.trim())}
                className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload Data'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CSVUploader;